/**
 * Build the `TimelineMarker` shape `findOverlayRanges` maps a graphic from.
 *
 * The transcript surfaces get markers from `useTimelineMarkers`, but two paths
 * have to synthesize one: the canvas actions layer (no marker list at all) and
 * the transcript insert (the real list is rebuilt on the next render, after the
 * generation has already fired). Both were assembling it inline with their own
 * casts; the rule for what makes a valid anchor lives here instead.
 *
 * An anchor is used only when BOTH indices are numbers — a half-anchor would
 * make `findOverlayRanges` map from a range with an undefined end. With no
 * usable anchor the marker carries only its time window, which is exactly the
 * fallback the underline already takes.
 *
 * PLAT-10921 DS14 — a `clip-start` / `clip-end` graphic covers MORE words than
 * the passage it was created from, so its anchor is recorded but not honoured.
 * Callers that know the placement pass it; the epic's E4 guarantee is that a
 * control's Selection scope resolves to the range the transcript underlines, and
 * that only holds if this agrees with `buildTimelineMarkers`.
 */
import { placementUsesTranscriptAnchor } from '@src/editor/lib/placementEvaluation';

import type { TimelineMarker } from '@src/components/transcript/hooks/useTimelineMarkers';

export interface OverlayMarkerAnchorInput {
  startInitialIndex?: unknown;
  endInitialIndex?: unknown;
}

export interface BuildOverlayMarkerParams {
  id: string;
  /** Element start, in seconds. Non-numeric input falls back to 0. */
  time?: unknown;
  /** Element end, in seconds. Non-numeric input falls back to `time`. */
  endTime?: unknown;
  /** The placement's stored `transcriptAnchor`, in whatever shape it persisted. */
  anchor?: OverlayMarkerAnchorInput | null;
  /**
   * The element's stored `placement`, when the caller has it. Omitted means
   * "keep the anchor" — the `selection` rebind paths in `placementAnchor.ts`
   * rely on that, and it is the pre-DS14 behaviour for anything that doesn't
   * know.
   */
  placement?: string | null;
}

export function buildOverlayMarker({
  id,
  time,
  endTime,
  anchor,
  placement,
}: BuildOverlayMarkerParams): TimelineMarker {
  const start = typeof time === 'number' && Number.isFinite(time) ? time : 0;
  const end =
    typeof endTime === 'number' && Number.isFinite(endTime) ? endTime : start;

  const marker: TimelineMarker = {
    id,
    type: 'overlay',
    time: start,
    endTime: end,
  };
  if (typeof placement === 'string') marker.placement = placement;

  if (
    placementUsesTranscriptAnchor(placement) &&
    typeof anchor?.startInitialIndex === 'number' &&
    typeof anchor?.endInitialIndex === 'number'
  ) {
    marker.anchor = {
      startInitialIndex: anchor.startInitialIndex,
      endInitialIndex: anchor.endInitialIndex,
    };
  }

  return marker;
}
